import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import { apiSlice } from "core/api";
import { makeCrudEndpoints, makeEndpoint } from "common/api/apiHelpers";
import type { Player, PlayerCreationPayload } from "quiz/types/player.model";

const api = apiSlice
  .injectEndpoints({
    endpoints: makeCrudEndpoints<"Player", Player>("Player", "players"),
  })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, "Player", any>) => ({
      ...makeEndpoint(
        "getPlayer",
        build.query<Player, [string, string]>({
          query: ([playerId, partitionId]) => ({
            method: "GET",
            url: `players/${partitionId}/${playerId}`,
          }),
          providesTags: (result, error, [partitionId, playerId]) => [
            { type: "Player", id: `${partitionId}/${playerId}` },
          ],
        }),
      ),
      ...makeEndpoint(
        "listPlayersQuery", // Renamed to avoid conflict with makeCrudEndpoints generated one
        build.query<
          Player[],
          {
            pronosticV2Id?: string;
            subPlaceId?: string;
            listOfSubPlaces?: string;
          }
        >({
          query: (filters) => {
            const params = new URLSearchParams();
            if (filters.pronosticV2Id) {
              params.append("pronosticV2Id", filters.pronosticV2Id);
            }
            if (filters.subPlaceId) {
              params.append("subPlaceId", filters.subPlaceId);
            }
            if (filters.listOfSubPlaces) {
              params.append("listOfSubPlaces", filters.listOfSubPlaces);
            }
            return {
              method: "GET",
              url: `players?${params.toString()}`,
            };
          },
          providesTags: (result, error, filters) => [
            {
              type: "Player",
              id: `LIST_PRONOSTIC_V2/${filters.pronosticV2Id}/${filters.subPlaceId || filters.listOfSubPlaces}`,
            },
          ],
        }),
      ),
    }),
    overrideExisting: true,
  })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, "Player", any>) =>
      makeEndpoint(
        "addPlayer",
        build.mutation<Player, PlayerCreationPayload>({
          query: ({ bingoGameId, playerInfo, pronosticV2Id, subPlaceId }) => ({
            method: "POST",
            url: `players`,
            body: { bingoGameId, playerInfo, pronosticV2Id, subPlaceId },
          }),
          invalidatesTags: [{ type: "Player", id: "LIST" }],
        }),
      ),
    overrideExisting: true,
  });

export const {
  useGetPlayerQuery,
  useListPlayersQuery: useListPlayersBaseQuery, // Renamed the original
  useAddPlayerMutation,
  useUpdatePlayerMutation,
  useListPlayersQuery, // Export the new one with the original name
} = api;
